import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { getProduct, ApiProduct } from '../services/productService';
import './styles/ProductPage.css';

const ProductPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState<ApiProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    async function fetchProduct() {
      if (!productId) {
        setError('Product not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getProduct(productId);
        setProduct(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load product');
      } finally {
        setLoading(false);
      }
    }

    fetchProduct();
  }, [productId]);

  const handleQuantityChange = (newQuantity: number) => {
    if (!product) return;
    const maxQty = product.inventory ?? 99;
    setQuantity(Math.max(1, Math.min(newQuantity, maxQty)));
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    // Map ApiProduct to Product type expected by cart
    const cartProduct = {
      id: product.productId,
      name: product.name,
      description: product.description,
      price: product.price,
      image: product.image,
      category: product.category,
      inventory: product.inventory,
      tags: product.tags
    };

    for (let i = 0; i < quantity; i++) {
      addToCart(cartProduct);
    }

    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="product-page">
        <div className="product-page__container">
          <div className="skeleton skeleton--breadcrumb" />
          <div className="product-page__layout">
            <div className="skeleton skeleton--image" />
            <div className="product-page__info">
              <div className="skeleton skeleton--badge" />
              <div className="skeleton skeleton--title" />
              <div className="skeleton skeleton--price" />
              <div className="skeleton skeleton--text" />
              <div className="skeleton skeleton--text short" />
              <div className="skeleton skeleton--actions" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-page">
        <div className="product-page__container">
          <div className="product-page__error">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="font-serif">Product Not Found</h2>
            <p>{error || "The product you're looking for doesn't exist."}</p>
            <Link to="/products">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Browse All Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isOutOfStock = product.inventory === 0;
  const isLowStock = product.inventory !== undefined && product.inventory > 0 && product.inventory < 5;
  const maxQuantity = product.inventory ?? 99;

  return (
    <div className="product-page">
      <div className="product-page__container">
        {/* Breadcrumb */}
        <nav className="product-page__breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/products">Products</Link>
          <span>/</span>
          <Link to={`/products?category=${encodeURIComponent(product.category)}`}>
            {product.category}
          </Link>
          <span>/</span>
          <span className="current">{product.name}</span>
        </nav>

        {/* Back Link - Below breadcrumbs, above product */}
        <div className="product-page__back">
          <Link to="/products">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Continue Shopping
          </Link>
        </div>

        {/* Main Layout */}
        <div className="product-page__layout">
          {/* Left: Image */}
          <div className="product-page__gallery">
            <div className="product-page__image-wrapper">
              <img src={product.image} alt={product.name} />
              {isOutOfStock && (
                <div className="product-page__stock-overlay">Out of Stock</div>
              )}
              {isLowStock && (
                <div className="product-page__low-stock">Only {product.inventory} left!</div>
              )}
            </div>
          </div>

          {/* Right: Info & Purchase */}
          <div className="product-page__info">
            <Link 
              to={`/products?category=${encodeURIComponent(product.category)}`}
              className="product-page__category"
            >
              {product.category}
            </Link>

            <h1 className="product-page__title font-serif">{product.name}</h1>

            <div className="product-page__price-row">
              <span className="product-page__price">${product.price.toFixed(2)}</span>
              <span className="product-page__currency">AUD</span>
            </div>

            {product.tags && product.tags.length > 0 && (
              <div className="product-page__tags">
                {product.tags.map(tag => (
                  <Link key={tag} to={`/products?tags=${encodeURIComponent(tag)}`}>
                    {tag}
                  </Link>
                ))}
              </div>
            )}

            <div className="product-page__description">
              <h3>Description</h3>
              <p>{product.description}</p>
            </div>

            {/* Purchase Section */}
            <div className="product-page__purchase">
              {/* Stock Status */}
              <div className={`product-page__stock product-page__stock--${isOutOfStock ? 'out' : isLowStock ? 'low' : 'in'}`}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isOutOfStock ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : isLowStock ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  )}
                </svg>
                {isOutOfStock ? 'Out of Stock' : isLowStock ? `Only ${product.inventory} left` : 'In Stock'}
              </div>

              {/* Quantity */}
              {!isOutOfStock && (
                <div className="product-page__quantity">
                  <label>Quantity</label>
                  <div className="product-page__quantity-controls">
                    <button onClick={() => handleQuantityChange(quantity - 1)} disabled={quantity <= 1}>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                      min={1}
                      max={maxQuantity}
                    />
                    <button onClick={() => handleQuantityChange(quantity + 1)} disabled={quantity >= maxQuantity}>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                  {maxQuantity < 10 && <span className="product-page__quantity-hint">{maxQuantity} available</span>}
                </div>
              )}

              {/* Buttons */}
              <div className="product-page__actions">
                <button 
                  className={`product-page__btn product-page__btn--cart ${addedToCart ? 'added' : ''}`}
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {addedToCart ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    )}
                  </svg>
                  {addedToCart ? 'Added to Cart!' : 'Add to Cart'}
                </button>

                <button 
                  className="product-page__btn product-page__btn--buy"
                  onClick={handleBuyNow}
                  disabled={isOutOfStock}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Buy Now
                </button>
              </div>

              {/* Trust Badges */}
              <div className="product-page__trust">
                <div>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Secure Checkout
                </div>
                <div>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  Handcrafted with Care
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;